import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  Typography,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";
import merge from "lodash/merge";
import BaseOptionChart from "../../../components/chart/BaseOptionChart";

// Mapeia chave do canal pra label amigável e cor representativa.
const CHANNEL_META = {
  whatsapp: { label: "WhatsApp", color: "#25D366" },
  telegram: { label: "Telegram", color: "#0088CC" },
  webchat: { label: "WebChat", color: "#6366f1" },
  instagram: { label: "Instagram", color: "#E1306C" },
  facebook: { label: "Facebook", color: "#1877F2" },
  email: { label: "E-mail", color: "#EA4335" },
  desconhecido: { label: "Outros", color: "#9ca3af" },
};

const ChannelsWidget = ({ data = [], loading = false }) => {
  const safe = useMemo(() => {
    return data
      .map((d) => {
        const key = (d.key || "desconhecido").toLowerCase();
        const meta = CHANNEL_META[key] || {
          label: d.key || "—",
          color: "#9ca3af",
        };
        return { ...d, key, ...meta };
      })
      .filter((d) => Number(d.count) > 0);
  }, [data]);

  const series = safe.map((d) => Number(d.count) || 0);
  const labels = safe.map((d) => d.label);
  const colors = safe.map((d) => d.color);
  const total = series.reduce((a, b) => a + b, 0);

  const options = useMemo(
    () =>
      merge(BaseOptionChart(), {
        colors,
        labels,
        legend: { position: "bottom", fontSize: "12px", markers: { radius: 4 } },
        dataLabels: { enabled: false },
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "Total",
                  fontSize: "12px",
                  formatter: () => `${total}`,
                },
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: (val) => {
              const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
              return `${val} (${pct}%)`;
            },
          },
        },
      }),
    [colors, labels, total]
  );

  return (
    <Card elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 2, height: "100%" }}>
      <CardHeader
        title="Tickets ativos por canal"
        titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
        subheader={
          total > 0
            ? `${total} ativo${total !== 1 ? "s" : ""} agora · pendentes + em atendimento`
            : "Sem tickets ativos no momento"
        }
        subheaderTypographyProps={{ variant: "caption" }}
      />
      <CardContent sx={{ pt: 0 }}>
        {loading ? (
          <Skeleton variant="circular" width={200} height={200} sx={{ mx: "auto" }} />
        ) : safe.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
            <Typography variant="body2">Sem dados</Typography>
          </Box>
        ) : (
          <ReactApexChart options={options} series={series} type="donut" height={260} />
        )}
      </CardContent>
    </Card>
  );
};

export default React.memo(ChannelsWidget);
